<template>
  <div class="g-page">
    <div class="content-container">
      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">
          <b-icon icon="clipboard-check-fill" class="title-icon"></b-icon>
          Tezkor Revizya
        </h2>
        <div class="header-actions">
          <button v-if="!revision" class="btn-primary" @click="startRevision()">
            <b-icon icon="plus-circle-fill"></b-icon> Yangi revizya
          </button>
          <button v-else class="btn-finalize" @click="finalizeRevision()" :disabled="finalizing">
            <b-icon icon="check-all"></b-icon>
            {{ finalizing ? 'Saqlanmoqda...' : 'Yakunlash + Tuzatish' }}
          </button>
        </div>
      </div>

      <!-- INFO + Hint banner -->
      <div v-if="revision && !revision.real" class="hint-banner">
        💡 <strong>Saqlash avtomatik</strong> — har son kiritilganda 0.5 sek ichida saqlanadi.
        Asosiy <a href="/#/revision/index">Revizya ro'yxatida</a> ko'rinishi uchun <strong>"Yakunlash"</strong> tugmasini bosing.
      </div>

      <!-- INFO -->
      <div v-if="revision" class="rev-info">
        <div class="ri-cell">
          <span class="ri-label">Revizya #</span>
          <strong>{{ revision.id }}</strong>
        </div>
        <div class="ri-cell">
          <span class="ri-label">Sklad</span>
          <strong>{{ revision.sklad ? revision.sklad.name : '—' }}</strong>
        </div>
        <div class="ri-cell">
          <span class="ri-label">Boshlangan</span>
          <strong>{{ formatDate(revision.created_at) }}</strong>
        </div>
        <div class="ri-cell" v-if="summary">
          <span class="ri-label">Mahsulotlar</span>
          <strong>{{ summary.items_count }} ta</strong>
        </div>
        <div class="ri-cell" v-if="summary">
          <span class="ri-label">Kamomad / Ortiqcha</span>
          <strong>
            <span v-if="summary.loss_count > 0" class="loss">−{{ summary.loss_count }}</span>
            <span v-if="summary.gain_count > 0" class="gain"> +{{ summary.gain_count }}</span>
            <span v-if="summary.loss_count === 0 && summary.gain_count === 0">—</span>
          </strong>
        </div>
        <div class="ri-cell" v-if="summary">
          <span class="ri-label">Farq summa</span>
          <strong :class="summary.total_difference_summa < 0 ? 'loss' : 'gain'">
            {{ formatMoney(summary.total_difference_summa) }}
          </strong>
        </div>
      </div>

      <!-- IF NO REVISION YET -->
      <div v-if="!revision" class="empty-state">
        <b-icon icon="clipboard-check" font-scale="4" class="empty-icon"></b-icon>
        <h4>Revizya boshlanmagan</h4>
        <p>"Yangi revizya" tugmasini bosing — ombor mahsulotlarini sanay boshlashingiz mumkin</p>
      </div>

      <!-- REVISION ACTIVE -->
      <div v-else class="revision-active">
        <!-- SEARCH + ADD -->
        <div class="search-bar">
          <div class="search-wrapper">
            <b-icon icon="search" class="search-icon"></b-icon>
            <input
              v-model="search"
              type="search"
              placeholder="Mahsulot nomi, kod yoki barcode..."
              class="search-input"
              @input="onSearch"
              ref="searchInput"
            />
          </div>
          <button class="search-btn" @click="loadProducts">
            <b-icon icon="arrow-repeat"></b-icon>
          </button>
        </div>

        <!-- TABS -->
        <div class="tabs">
          <button :class="['tab', { active: tab === 'all' }]" @click="tab = 'all'">
            📦 Hammasi ({{ products.length }})
          </button>
          <button :class="['tab', { active: tab === 'counted' }]" @click="tab = 'counted'">
            ✅ Sanalganlar ({{ countedItems.length }})
          </button>
          <button :class="['tab', { active: tab === 'diff' }]" @click="tab = 'diff'">
            ⚠️ Farqli ({{ diffItems.length }})
          </button>
        </div>

        <!-- PRODUCTS TABLE -->
        <div v-if="tab === 'all'" class="table-card">
          <div class="table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th style="width:50px">#</th>
                  <th>Mahsulot</th>
                  <th>Barcode</th>
                  <th class="num">Sistemada</th>
                  <th class="num">Haqiqatda</th>
                  <th class="num">Farq</th>
                  <th style="width:80px;text-align:center">Amal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, i) in products" :key="p.id"
                    :class="rowDiffClass(p)">
                  <td>{{ i + 1 }}</td>
                  <td class="name-cell">
                    <div class="prod-name">{{ p.name }}</div>
                    <div class="prod-extra">{{ p.extra_name }} · #{{ p.number }}</div>
                  </td>
                  <td class="muted small">{{ p.shtrix_kod || '—' }}</td>
                  <td class="num">
                    <span class="balance-badge">{{ p.balance }}</span>
                  </td>
                  <td class="num">
                    <div class="input-wrap">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        v-model.number="realCounts[p.id]"
                        @input="onCountInput(p)"
                        @blur="saveCount(p)"
                        class="real-input"
                        :class="{ 'is-saving': isSaving(p), 'is-saved': isSaved(p) }"
                        :placeholder="String(p.balance)"
                      />
                      <span v-if="isSaving(p)" class="status-icon saving" title="Saqlanmoqda...">⏳</span>
                      <span v-else-if="isSaved(p)" class="status-icon saved" title="Saqlandi">✓</span>
                    </div>
                  </td>
                  <td class="num">
                    <span v-if="hasCount(p) && diff(p) !== 0" :class="diffClass(diff(p))" class="diff-val">
                      {{ diff(p) > 0 ? '+' : '' }}{{ diff(p) }}
                    </span>
                    <span v-else-if="hasCount(p) && diff(p) === 0" class="ok">✓</span>
                    <span v-else class="muted">—</span>
                  </td>
                  <td style="text-align:center">
                    <button v-if="hasCount(p)" class="icon-btn" @click="resetCount(p)" title="Tozalash">
                      <b-icon icon="x-circle"></b-icon>
                    </button>
                  </td>
                </tr>
                <tr v-if="loading">
                  <td colspan="7" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
                </tr>
                <tr v-if="!loading && products.length === 0">
                  <td colspan="7" class="empty-row">Mahsulot topilmadi</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- COUNTED TAB -->
        <div v-if="tab === 'counted'" class="table-card">
          <div class="table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th style="width:50px">#</th>
                  <th>Mahsulot</th>
                  <th class="num">Sistemada</th>
                  <th class="num">Sanalgan</th>
                  <th class="num">Farq</th>
                  <th class="num">Summa farqi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(it, i) in countedItems" :key="it.id" :class="rowItemClass(it)">
                  <td>{{ i + 1 }}</td>
                  <td class="name-cell">
                    <div class="prod-name">{{ it.product ? it.product.name : '—' }}</div>
                    <div class="prod-extra">#{{ it.product ? it.product.number : '' }}</div>
                  </td>
                  <td class="num"><span class="balance-badge">{{ it.count }}</span></td>
                  <td class="num bold">{{ it.real_count }}</td>
                  <td class="num">
                    <span :class="diffClass(it.difference)">
                      {{ it.difference > 0 ? '+' : '' }}{{ it.difference }}
                    </span>
                  </td>
                  <td class="num" :class="it.difference_summa < 0 ? 'loss' : it.difference_summa > 0 ? 'gain' : 'muted'">
                    {{ formatMoney(it.difference_summa) }}
                  </td>
                </tr>
                <tr v-if="countedItems.length === 0">
                  <td colspan="6" class="empty-row">Hali hech narsa sanalmagan</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- DIFF TAB -->
        <div v-if="tab === 'diff'" class="table-card">
          <div class="table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th style="width:50px">#</th>
                  <th>Mahsulot</th>
                  <th class="num">Sistemada</th>
                  <th class="num">Haqiqatda</th>
                  <th class="num">Farq</th>
                  <th class="num">Summa</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(it, i) in diffItems" :key="it.id" :class="rowItemClass(it)">
                  <td>{{ i + 1 }}</td>
                  <td class="name-cell">
                    <div class="prod-name">{{ it.product ? it.product.name : '—' }}</div>
                  </td>
                  <td class="num"><span class="balance-badge">{{ it.count }}</span></td>
                  <td class="num bold">{{ it.real_count }}</td>
                  <td class="num">
                    <span :class="diffClass(it.difference)" class="big">
                      {{ it.difference > 0 ? '+' : '' }}{{ it.difference }}
                    </span>
                  </td>
                  <td class="num bold" :class="it.difference_summa < 0 ? 'loss' : 'gain'">
                    {{ formatMoney(it.difference_summa) }}
                  </td>
                </tr>
                <tr v-if="diffItems.length === 0">
                  <td colspan="6" class="empty-row">Farq yo'q — hammasi to'g'ri</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
  name: 'RevisionQuick',
  data() {
    return {
      revision: null,
      summary: null,
      items: [],
      itemsByProduct: {},   // O(1) lookup: { product_id: item }
      products: [],
      realCounts: {},      // { product_id: real_count }
      tab: 'all',          // all | counted | diff
      search: '',
      loading: false,
      finalizing: false,
      savingProductId: null,    // qaysi mahsulot saqlanyapti
      savedProductId: null,     // qaysi mahsulot endigina saqlandi (yashil ✓)
      saveTimers: {},           // har product uchun debounce timer
      restoringFromServer: false, // serverdan yuklash paytida watch ishlamasligi uchun
    };
  },
  computed: {
    countedItems() {
      return this.items.filter(i => Number(i.real_count) > 0 || Number(i.real_count) === 0 && i.id);
    },
    diffItems() {
      return this.items.filter(i => Number(i.difference) !== 0);
    },
  },
  methods: {
    formatMoney(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + " so'm";
    },
    formatDate(unix) {
      if (!unix) return '—';
      const d = new Date(unix * 1000);
      return d.toLocaleDateString('uz-UZ') + ' ' + d.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' });
    },
    hasCount(p) {
      return this.realCounts[p.id] !== undefined && this.realCounts[p.id] !== null && this.realCounts[p.id] !== '';
    },
    diff(p) {
      const real = Number(this.realCounts[p.id] || 0);
      return real - Number(p.balance || 0);
    },
    diffClass(d) {
      if (d > 0) return 'gain';
      if (d < 0) return 'loss';
      return 'ok';
    },
    rowDiffClass(p) {
      if (!this.hasCount(p)) return '';
      const d = this.diff(p);
      if (d === 0) return 'row-ok';
      if (d < 0) return 'row-loss';
      return 'row-gain';
    },
    rowItemClass(it) {
      const d = parseFloat(it.difference || 0);
      if (d === 0) return 'row-ok';
      if (d < 0) return 'row-loss';
      return 'row-gain';
    },

    async startRevision() {
      try {
        const res = await axios.post('api/v1/revision/quick-create', {
          comment: 'Tezkor revizya — ' + new Date().toLocaleString('uz-UZ'),
        });
        await this.loadRevision(res.data.id);
        await this.loadProducts();
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      } catch (e) {
        alert('Xato: ' + (e.response?.data?.message || e.message));
      }
    },

    async loadRevision(id, preserveLocal = false) {
      const res = await axios.get(`api/v1/revision/${id}/items?with_zero=1`);
      this.revision = res.data.revision;
      this.summary = res.data.summary;
      this.items = res.data.items;

      // O(1) lookup map
      const map = {};
      this.items.forEach(it => { map[it.product_id] = it; });
      this.itemsByProduct = map;

      // realCounts'ni faqat birinchi yuklashda yoki preserveLocal=false bo'lganda almashtiramiz
      // Aks holda foydalanuvchi yozayotgan inputlar buzilmasligi uchun saqlaymiz
      if (!preserveLocal) {
        this.restoringFromServer = true;
        const counts = {};
        this.items.forEach(it => {
          counts[it.product_id] = parseFloat(it.real_count);
        });
        this.realCounts = counts;
        this.$nextTick(() => { this.restoringFromServer = false; });
      }
    },

    recalcSummary() {
      // Lokal summary'ni qayta hisoblash (server chaqirmasdan)
      let totalCount = 0, totalReal = 0, totalDiff = 0, totalDiffSumma = 0;
      let lossCount = 0, gainCount = 0;
      this.items.forEach(i => {
        totalCount += parseFloat(i.count || 0);
        totalReal  += parseFloat(i.real_count || 0);
        const d = parseFloat(i.difference || 0);
        totalDiff += d;
        totalDiffSumma += parseFloat(i.difference_summa || 0);
        if (d < 0) lossCount++;
        if (d > 0) gainCount++;
      });
      this.summary = {
        items_count: this.items.length,
        total_count: totalCount,
        total_real_count: totalReal,
        total_difference: totalDiff,
        total_difference_summa: totalDiffSumma,
        loss_count: lossCount,
        gain_count: gainCount,
      };
    },

    async loadProducts() {
      this.loading = true;
      try {
        const params = { limit: 200, search: this.search || undefined };
        const res = await axios.get('api/v1/revision/products-list', { params });
        this.products = res.data.items || [];
      } finally {
        this.loading = false;
      }
    },

    onSearch: debounce(function () {
      this.loadProducts();
    }, 350),

    /**
     * Input o'zgarganda chaqiriladi — debounce bilan auto-save
     * Foydalanuvchi yozayotgan paytda har 500ms da bir saqlaydi
     */
    onCountInput(p) {
      if (this.restoringFromServer) return;
      // Har product uchun alohida timer
      if (this.saveTimers[p.id]) clearTimeout(this.saveTimers[p.id]);
      this.saveTimers[p.id] = setTimeout(() => {
        this.saveCount(p);
      }, 500);
    },

    async saveCount(p) {
      if (!this.revision) return;
      const real = Number(this.realCounts[p.id]);
      if (isNaN(real) || real < 0) return;

      this.savingProductId = p.id;
      try {
        const res = await axios.post(`api/v1/revision/${this.revision.id}/scan`, {
          product_id: p.id,
          real_count: real,
        });
        const newItem = res.data.item;

        // Lokal items array'ni yangilash (loadRevision chaqirmasdan!)
        const idx = this.items.findIndex(i => i.product_id === p.id);
        if (idx >= 0) {
          this.$set(this.items, idx, newItem);
        } else {
          this.items.unshift(newItem);
        }
        this.$set(this.itemsByProduct, p.id, newItem);

        // Summary'ni lokal qayta hisoblash
        this.recalcSummary();

        // Saqlandi indikatori (1.5 sek)
        this.savedProductId = p.id;
        setTimeout(() => {
          if (this.savedProductId === p.id) this.savedProductId = null;
        }, 1500);
      } catch (e) {
        console.error('Saqlashda xato:', e);
        alert('Xato: ' + (e.response?.data?.message || e.message));
      } finally {
        this.savingProductId = null;
      }
    },

    async resetCount(p) {
      // Mavjud item'ni o'chirish
      const item = this.itemsByProduct[p.id];
      if (item && this.revision) {
        try {
          await axios.delete(`api/v1/revision/${this.revision.id}/items/${item.id}`);
          // Lokal items'dan ham olib tashlash
          this.items = this.items.filter(i => i.product_id !== p.id);
          this.$delete(this.itemsByProduct, p.id);
          this.recalcSummary();
        } catch (e) { /* ignore */ }
      }
      this.$delete(this.realCounts, p.id);
    },

    isSaving(p) { return this.savingProductId === p.id; },
    isSaved(p)  { return this.savedProductId  === p.id; },

    async finalizeRevision() {
      if (!this.revision) return;
      const ok = confirm(
        `Revizyani yakunlaymizmi?\n\n` +
        `Sanalgan mahsulotlar: ${this.summary?.items_count || 0} ta\n` +
        `Farq: ${this.summary?.total_difference || 0}\n` +
        `Bu amal qaytarib bo'lmaydi — ombordagi qoldiqlar tuzatiladi.`
      );
      if (!ok) return;

      this.finalizing = true;
      try {
        const res = await axios.post(`api/v1/revision/${this.revision.id}/finalize`);
        alert(`✅ Yakunlandi!\n\nTuzatishlar: ${res.data.corrections_applied} ta\nJami farq summa: ${this.formatMoney(res.data.total_summa)}`);
        // Yangi revizya rejimiga qaytamiz
        this.revision = null;
        this.summary = null;
        this.items = [];
        this.realCounts = {};
        this.products = [];
      } catch (e) {
        alert('Xato: ' + (e.response?.data?.message || e.message));
      } finally {
        this.finalizing = false;
      }
    },
  },
};
</script>

<style scoped>
.g-page { padding: 20px; height: 100%; overflow-y: auto; background: #f8fafc; }
.content-container { max-width: 1500px; margin: 0 auto; }

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}
.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
}
.title-icon { color: #2563eb; font-size: 26px; }

.btn-primary {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 12px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(37,99,235,0.25);
}

.btn-finalize {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: white;
  border: none;
  padding: 10px 22px;
  border-radius: 12px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  box-shadow: 0 4px 14px rgba(22,163,74,0.35);
}
.btn-finalize:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }

/* INFO */
.rev-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  background: white;
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  margin-bottom: 14px;
}
.ri-cell { display: flex; flex-direction: column; }
.ri-label { font-size: 11px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.ri-cell strong { font-size: 16px; font-weight: 800; color: #1e293b; margin-top: 2px; }

/* EMPTY STATE */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 14px;
}
.empty-icon { color: #cbd5e1; margin-bottom: 16px; }
.empty-state h4 { font-weight: 800; color: #475569; margin: 0 0 6px 0; }
.empty-state p { color: #94a3b8; margin: 0; }

/* SEARCH BAR */
.search-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.search-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 16px;
}
.search-input {
  width: 100%;
  background: white;
  border: 1px solid #e2e8f0;
  padding: 12px 16px 12px 42px;
  border-radius: 12px;
  font-size: 15px;
  outline: none;
  transition: 0.2s;
}
.search-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37,99,235,0.08);
}
.search-btn {
  background: white;
  border: 1px solid #e2e8f0;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  cursor: pointer;
  color: #2563eb;
}

/* TABS */
.tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}
.tab {
  padding: 9px 18px;
  border-radius: 10px;
  border: 2px solid transparent;
  background: white;
  color: #64748b;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: 0.15s;
}
.tab.active {
  border-color: #2563eb;
  color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 2px 8px rgba(37,99,235,0.12);
}

/* TABLE */
.table-card {
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  overflow: hidden;
}
.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 360px);
  overflow-y: auto;
}
.custom-table {
  width: 100%;
  border-collapse: collapse;
}
.custom-table thead { position: sticky; top: 0; z-index: 2; }
.custom-table th {
  background: #f8fafc;
  padding: 12px 14px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
  text-align: left;
}
.custom-table th.num,
.custom-table td.num { text-align: right; }
.custom-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f8fafc;
  font-size: 14px;
  vertical-align: middle;
}

.row-ok td   { background: #f0fdf4; }
.row-loss td { background: #fef2f2; }
.row-gain td { background: #fef3c7; }

.name-cell .prod-name { font-weight: 700; color: #1e293b; }
.name-cell .prod-extra { font-size: 11px; color: #94a3b8; margin-top: 2px; }

.muted { color: #94a3b8; }
.small { font-size: 12px; }
.bold { font-weight: 800; color: #1e293b; }
.big { font-size: 16px; font-weight: 800; }

.balance-badge {
  background: #eff6ff;
  color: #1d4ed8;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 13px;
}

.input-wrap {
  position: relative;
  display: inline-block;
}

.real-input {
  width: 90px;
  padding: 6px 24px 6px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  text-align: right;
  font-weight: 700;
  font-size: 14px;
  outline: none;
  background: white;
  transition: 0.2s;
}
.real-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
}
.real-input.is-saving { border-color: #f59e0b; background: #fffbeb; }
.real-input.is-saved  { border-color: #16a34a; background: #f0fdf4; }

.status-icon {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  pointer-events: none;
}
.status-icon.saving { color: #f59e0b; animation: spin 1s linear infinite; }
.status-icon.saved  { color: #16a34a; font-weight: 800; }

.diff-val { font-size: 14px; font-weight: 800; }

/* HINT BANNER */
.hint-banner {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fcd34d;
  border-radius: 12px;
  padding: 10px 16px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #92400e;
  font-weight: 600;
}
.hint-banner a {
  color: #b45309;
  text-decoration: underline;
  font-weight: 700;
}
.hint-banner strong { color: #78350f; }

.gain { color: #16a34a; font-weight: 800; }
.loss { color: #dc2626; font-weight: 800; }
.ok   { color: #16a34a; font-weight: 700; }

.icon-btn {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
}
.icon-btn:hover { background: #ef4444; color: white; }

.loading-row { text-align: center; padding: 30px; color: #2563eb; }
.empty-row { text-align: center; padding: 50px; color: #94a3b8; font-style: italic; }
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #dbeafe;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
